// src/Home.js
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useState, useEffect, useCallback } from 'react';
import { SITE_CONFIG } from './config';
import { useTheme } from './App';

function Home() {
  const { theme } = useTheme();
  const [deals, setDeals] = useState([]);
  const [scrollPosition, setScrollPosition] = useState(0);
  const API_URL = SITE_CONFIG.apiUrl;

  // Helper function to replace affiliate tag in URLs
  const updateAffiliateTag = (url) => {
    if (!url) return url;

    const oldTags = [
      'dealsniper0d-20',
      'odealia0d-20',
      'dealhub0d-20',
    ];

    let updatedUrl = url;

    oldTags.forEach(oldTag => {
      const tagPattern = new RegExp(`tag=${oldTag}`, 'gi');
      updatedUrl = updatedUrl.replace(tagPattern, `tag=${SITE_CONFIG.affiliateTag}`);
    });

    if (!updatedUrl.includes('tag=')) {
      const separator = updatedUrl.includes('?') ? '&' : '?';
      updatedUrl += `${separator}tag=${SITE_CONFIG.affiliateTag}`;
    }

    return updatedUrl;
  };

  // Helper function to process deals and update affiliate tags
  const processDeals = useCallback((rawDeals) => {
    return rawDeals.map(deal => ({
      ...deal,
      url: updateAffiliateTag(deal.url)
    }));
  }, []);

  // Fetch recent deals
  useEffect(() => {
    const fetchDeals = () => {
      fetch(`${API_URL}/v2/deals?sort=recent&limit=20`)
        .then(response => {
          if (!response.ok) throw new Error('Network response was not ok');
          return response.json();
        })
        .then(data => {
          const processedDeals = processDeals(data);
          setDeals(processedDeals);
        })
        .catch(error => {
          console.error('Error fetching deals:', error);
        });
    };

    fetchDeals();
    const interval = setInterval(fetchDeals, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, [API_URL, processDeals]);

  // Auto-scroll effect
  useEffect(() => {
    const scrollInterval = setInterval(() => {
      setScrollPosition(prev => prev + 1);
    }, 50); // Scroll every 50ms for smooth animation

    return () => clearInterval(scrollInterval);
  }, []);

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.primary.bg} ${theme.primary.text}`}>
      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="flex flex-col items-center justify-center min-h-96 p-4 md:p-6 text-center relative overflow-hidden"
      >
        {/* Background gradient overlay */}
        <div className={`absolute inset-0 bg-gradient-to-r ${theme.gradients.hero} backdrop-blur-3xl opacity-50`}></div>

        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className={`text-3xl md:text-6xl font-extrabold mb-4 md:mb-6 bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent drop-shadow-2xl relative z-10`}
        >
          {SITE_CONFIG.content.home.heroTitle}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className={`text-sm md:text-lg ${theme.primary.textSecondary} max-w-2xl mb-6 md:mb-8 ${theme.primary.card} p-3 md:p-4 rounded-2xl border shadow-2xl relative z-10`}
        >
          {SITE_CONFIG.content.home.heroSubtitle}
        </motion.p>
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="relative z-10"
        >
          <Link
            to="/deals/amazon"
            className={`bg-gradient-to-r ${theme.primary.button} text-white px-4 py-2 md:px-8 md:py-4 rounded-full hover:${theme.primary.buttonHover} transition-all duration-300 shadow-2xl backdrop-blur-sm border border-white/20 font-semibold text-sm md:text-lg inline-block`}
          >
            {SITE_CONFIG.content.home.heroButton}
          </Link>
        </motion.div>
      </motion.section>

      {/* Stylish divider */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.9 }}
        className="flex justify-center my-8 md:my-12"
      >
        <div className={`w-40 h-1 bg-gradient-to-r ${theme.primary.accent} rounded-full shadow-lg`}></div>
      </motion.div>

      {/* Who This Is For Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.0 }}
        className="p-6 md:p-12 relative"
      >
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className={`text-2xl md:text-4xl font-bold text-center mb-8 md:mb-12 bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent`}
        >
          {SITE_CONFIG.content.home.whoThisIsFor.title}
        </motion.h2>
        <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12 mx-auto max-w-6xl">
          <div className="grid grid-cols-1 gap-6 md:gap-8 max-w-4xl">
            {SITE_CONFIG.content.home.whoThisIsFor.items.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.2 + (index * 0.2), duration: 0.8 }}
                className={`flex items-center space-x-4 md:space-x-6 ${theme.primary.card} p-4 md:p-6 rounded-2xl border shadow-2xl hover:${theme.primary.cardHover} transition-all duration-300`}
              >
                <span className={`w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r ${theme.primary.button} rounded-full flex items-center justify-center text-white text-lg md:text-xl shadow-lg flex-shrink-0`}>
                  {item.icon}
                </span>
                <p className={`text-sm md:text-lg ${theme.primary.textSecondary} leading-relaxed`}>{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Stylish divider */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.9 }}
        className="flex justify-center my-8 md:my-12"
      >
        <div className={`w-40 h-1 bg-gradient-to-r ${theme.primary.accent} rounded-full shadow-lg`}></div>
      </motion.div>

      {/* Recent Deals Feed */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 2.0 }}
        className="p-6 md:p-12"
      >
        <div className={`${theme.primary.card} p-6 rounded-2xl border shadow-xl max-w-2xl mx-auto mb-8`}>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className={`text-2xl md:text-4xl font-bold text-center mb-3 bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent`}
          >
            Latest Deals
          </motion.h2>
          <p className={`text-center ${theme.primary.text} text-sm md:text-lg font-medium`}>Curated daily just for you</p>
        </div>

        <div className="relative overflow-hidden h-64 md:h-96">
          <motion.div
            className="flex space-x-4 md:space-x-6"
            animate={{ x: -scrollPosition * 2 }}
            style={{
              width: `${deals.length * 320}px`,
            }}
          >
            {deals.concat(deals).map((deal, index) => (
              <motion.div
                key={`${deal.id || deal.url || deal.timestamp}-${index}`}
                className={`min-w-[250px] md:min-w-[300px] ${theme.primary.card} rounded-2xl border shadow-2xl p-3 md:p-4 hover:${theme.primary.cardHover} transition-all duration-300`}
                whileHover={{ scale: 1.02, y: -2 }}
              >
                <div className={`bg-gradient-to-r ${theme.primary.discountBadge} text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg mb-2 md:mb-3 w-fit`}>
                  {deal.discount}% Off
                </div>
                {deal.image_url && (
                  <img
                    src={deal.image_url}
                    alt={deal.title}
                    className={`w-full h-24 md:h-32 object-contain rounded-xl mb-2 md:mb-3 ${theme.primary.card} p-2`}
                  />
                )}
                <p className={`text-xs md:text-sm font-medium ${theme.primary.text} truncate mb-2 md:mb-3 leading-relaxed`}>
                  {deal.title}
                </p>
                <button
                  onClick={() => window.open(deal.url, '_blank')}
                  className={`bg-gradient-to-r ${theme.primary.button} text-white px-3 py-1.5 md:px-4 md:py-2 rounded-full hover:${theme.primary.buttonHover} transition-all duration-300 w-full mb-2 md:mb-3 shadow-lg text-xs md:text-sm font-semibold`}
                >
                  Get Deal →
                </button>
                <p className="text-sm md:text-lg font-bold text-green-400">Now: ${deal.current_price}</p>
                <p className={`text-xs ${theme.primary.textMuted} line-through`}>Was: ${deal.regular_price}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        <div className="text-center mt-6 md:mt-8">
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 3.0 }}
            className={`flex flex-col md:flex-row gap-3 md:gap-4 justify-center items-center`}
          >
            <Link
              to="/deals"
              className={`bg-gradient-to-r ${theme.primary.button} text-white px-4 py-2 md:px-8 md:py-4 rounded-full hover:${theme.primary.buttonHover} transition-all duration-300 shadow-2xl backdrop-blur-sm border border-white/20 font-semibold text-sm md:text-lg inline-block`}
            >
              View All Deals
            </Link>
            {/* Conditionally render Discord button */}
            {SITE_CONFIG.display.navbar.discordButton && (
              <a
                href={SITE_CONFIG.social.discord}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-rose-500 to-pink-600 text-white px-4 py-2 md:px-8 md:py-4 rounded-full hover:from-rose-600 hover:to-pink-700 transition-all duration-300 shadow-2xl backdrop-blur-sm border border-white/20 font-semibold text-sm md:text-lg inline-block"
              >
                Join the FREE Server
              </a>
            )}
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
}

export default Home;
